import XLSX from "xlsx-js-style";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
} from "../types/preflight.types";
import {
  EMPTY_CELL_TOKEN,
  assetExportFilename,
  assetLabel,
  exportFilename,
  formatNotes,
  formatScore,
  formatWeight,
  humanizeBrand,
  humanizeState,
  marketLabel,
  singleAssetReport,
} from "./export-report";
import { featureDetails, featureDisplayState, visibleFeatures } from "./feature-format";
import { downloadBytes } from "./download";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ─── Palette (shared with the server-side scripts workbook for brand parity) ───
const NAVY = "FF1F3A5F";
const SLATE = "FF44546A";
const GROUP_FILL = "FFEEF4FB";
const WHITE = "FFFFFFFF";
const OFF_WHITE = "FFF5F7FA";
const TEXT_DARK = "FF1F2937";
const TEXT_GRAY = "FF6B7280";
const LINK_BLUE = "FF1F6FEB";
const BORDER_GRAY = "FFD0D0D0";

/** Bold accent colors for the Status column, keyed by raw feature state. */
const STATUS_COLORS: Record<string, string> = {
  PASS: "FF15803D",
  FAIL: "FFB91C1C",
  PARTIAL: "FFB45309",
};

type BorderSide = { style: string; color: { rgb: string } };
interface CellStyle {
  fill?: { patternType: string; fgColor: { rgb: string } };
  font?: {
    name?: string;
    sz?: number;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: { rgb: string };
  };
  alignment?: { horizontal?: string; vertical?: string; wrapText?: boolean };
  border?: {
    top?: BorderSide;
    bottom?: BorderSide;
    left?: BorderSide;
    right?: BorderSide;
  };
}

const thin = (rgb = BORDER_GRAY): BorderSide => ({ style: "thin", color: { rgb } });
const ALL_BORDERS = { top: thin(), bottom: thin(), left: thin(), right: thin() };
const solid = (rgb: string) => ({ patternType: "solid", fgColor: { rgb } });

const S = {
  title: {
    fill: solid(NAVY),
    font: { name: "Calibri", sz: 16, bold: true, color: { rgb: WHITE } },
    alignment: { vertical: "center", wrapText: true },
  },
  subtitle: {
    fill: solid(OFF_WHITE),
    font: { name: "Calibri", sz: 11, italic: true, color: { rgb: TEXT_GRAY } },
    alignment: { vertical: "center" },
  },
  colHeader: {
    fill: solid(SLATE),
    font: { name: "Calibri", sz: 11, bold: true, color: { rgb: WHITE } },
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    border: ALL_BORDERS,
  },
  metaLabel: {
    fill: solid(SLATE),
    font: { name: "Calibri", sz: 11, bold: true, color: { rgb: WHITE } },
    alignment: { vertical: "center" },
    border: ALL_BORDERS,
  },
  metaValue: {
    fill: solid(WHITE),
    font: { name: "Calibri", sz: 11, color: { rgb: TEXT_DARK } },
    alignment: { vertical: "center", wrapText: true },
    border: ALL_BORDERS,
  },
} satisfies Record<string, CellStyle>;

interface BodyOpts {
  bold?: boolean;
  color?: string;
  underline?: boolean;
  align?: string;
  valign?: string;
}

function bodyStyle(fillRgb: string, opts: BodyOpts = {}): CellStyle {
  return {
    fill: solid(fillRgb),
    font: {
      name: "Calibri",
      sz: 11,
      bold: opts.bold ?? false,
      underline: opts.underline ?? false,
      color: { rgb: opts.color ?? TEXT_DARK },
    },
    alignment: {
      horizontal: opts.align ?? "left",
      vertical: opts.valign ?? "top",
      wrapText: true,
    },
    border: ALL_BORDERS,
  };
}

// ─── Sheet primitives (mirrors services/api/src/lib/scriptsXlsx.ts) ────────────

function setCell(
  ws: XLSX.WorkSheet,
  r: number,
  c: number,
  value: string | number,
  style: CellStyle,
  link?: string,
): void {
  const addr = XLSX.utils.encode_cell({ r, c });
  const cell: XLSX.CellObject = {
    t: typeof value === "number" ? "n" : "s",
    v: value,
    s: style,
  };
  if (link) cell.l = { Target: link, Tooltip: "Open asset" };
  ws[addr] = cell;
}

function styleRange(
  ws: XLSX.WorkSheet,
  r: number,
  cStart: number,
  cEnd: number,
  style: CellStyle,
): void {
  for (let c = cStart; c <= cEnd; c++) {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) ws[addr] = { t: "s", v: "", s: style };
    else ws[addr].s = style;
  }
}

function mergeRange(
  ws: XLSX.WorkSheet,
  rStart: number,
  cStart: number,
  rEnd: number,
  cEnd: number,
): void {
  if (!ws["!merges"]) ws["!merges"] = [];
  ws["!merges"].push({ s: { r: rStart, c: cStart }, e: { r: rEnd, c: cEnd } });
}

function updateRef(ws: XLSX.WorkSheet): void {
  const merges = ws["!merges"] ?? [];
  let maxR = 0;
  let maxC = 0;
  for (const addr of Object.keys(ws).filter((k) => !k.startsWith("!"))) {
    const decoded = XLSX.utils.decode_cell(addr);
    if (decoded.r > maxR) maxR = decoded.r;
    if (decoded.c > maxC) maxC = decoded.c;
  }
  for (const m of merges) {
    if (m.e.r > maxR) maxR = m.e.r;
    if (m.e.c > maxC) maxC = m.e.c;
  }
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** SNAKE_CASE enum → "Sentence case" (report/asset lifecycle + band values). */
function titleCase(value: string): string {
  const lower = value.replace(/_/g, " ").toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// ─── Summary sheet (report stats live here, separate from the data table) ──────

function buildSummarySheet(report: PreflightReportDetail): XLSX.WorkSheet {
  const ws: XLSX.WorkSheet = {};
  const LAST_COL = 1;
  let r = 0;

  setCell(ws, r, 0, report.name, S.title);
  styleRange(ws, r, 1, LAST_COL, S.title);
  mergeRange(ws, r, 0, r, LAST_COL);
  r += 1;

  setCell(ws, r, 0, `PreFlight report · Exported ${isoToday()}`, S.subtitle);
  styleRange(ws, r, 1, LAST_COL, S.subtitle);
  mergeRange(ws, r, 0, r, LAST_COL);
  r += 2; // blank spacer row

  const stats: Array<[string, string | number]> = [
    ["Brand", humanizeBrand(report.brandName)],
    ["Platform", report.platform],
    ["Placement", report.placement],
    ["Market", marketLabel(report)],
  ];
  if (report.overallScore != null) {
    const band = report.overallBand ? ` (${titleCase(report.overallBand)})` : "";
    stats.push(["Overall Score", `${report.overallScore} / 100${band}`]);
  }
  if (report.createdBy) stats.push(["Created By", report.createdBy.displayName]);
  stats.push(["Created", new Date(report.createdAt).toISOString().slice(0, 10)]);
  stats.push(["Report Status", titleCase(report.status)]);
  stats.push(["Assets", report.assetCount]);
  stats.push(["Scored", report.scoredCount]);
  if (report.failedCount > 0) stats.push(["Failed", report.failedCount]);
  if (report.criteriaPassedCount > 0)
    stats.push(["Criteria Passed", report.criteriaPassedCount]);
  if (report.criteriaFailedCount > 0)
    stats.push(["Criteria Failed", report.criteriaFailedCount]);

  for (const [label, value] of stats) {
    setCell(ws, r, 0, label, S.metaLabel);
    setCell(ws, r, 1, value, S.metaValue);
    r += 1;
  }

  ws["!cols"] = [{ wch: 18 }, { wch: 52 }];
  ws["!rows"] = [{ hpt: 34 }];
  ws["!sheetViews"] = [{ showGridLines: false }];
  updateRef(ws);
  return ws;
}

// ─── Criteria sheet (one block per asset, file identity shown once) ────────────

const CRITERIA_COLUMNS = [
  "Asset",
  "Download",
  "Asset Score",
  "Criterion",
  "Status",
  "Weight",
  "Notes",
] as const;
const LAST_CRITERIA_COL = CRITERIA_COLUMNS.length - 1;

function statusStyle(state: string, fillRgb: string): CellStyle {
  return bodyStyle(fillRgb, {
    bold: true,
    color: STATUS_COLORS[state] ?? TEXT_GRAY,
  });
}

function setDownloadCell(
  ws: XLSX.WorkSheet,
  r: number,
  link: string | null,
  fillRgb: string,
): void {
  if (link) {
    setCell(
      ws,
      r,
      1,
      "Download",
      bodyStyle(fillRgb, { color: LINK_BLUE, underline: true, valign: "center" }),
      link,
    );
  } else {
    setCell(ws, r, 1, EMPTY_CELL_TOKEN, bodyStyle(fillRgb, { valign: "center" }));
  }
}

function buildCriteriaSheet(report: PreflightReportDetail): XLSX.WorkSheet {
  const ws: XLSX.WorkSheet = {};
  let r = 0;

  setCell(
    ws,
    r,
    0,
    `Criteria breakdown — ${report.platform} · ${report.placement}`,
    S.title,
  );
  styleRange(ws, r, 1, LAST_CRITERIA_COL, S.title);
  mergeRange(ws, r, 0, r, LAST_CRITERIA_COL);
  r += 1;

  CRITERIA_COLUMNS.forEach((header, c) => setCell(ws, r, c, header, S.colHeader));
  r += 1;

  report.assets.forEach((asset, groupIndex) => {
    const fillRgb = groupIndex % 2 === 0 ? WHITE : GROUP_FILL;
    const base = bodyStyle(fillRgb);
    const groupCell = bodyStyle(fillRgb, { valign: "center" });
    const label = assetLabel(asset);
    const link = asset.assetUrl ?? asset.sourceUrl ?? null;
    const startR = r;

    // Only the criteria users see (AD-1749) — hidden no-verdict criteria stay out of the sheet.
    const features = visibleFeatures(asset.features);

    if (features.length === 0) {
      setCell(ws, r, 0, label, groupCell);
      setDownloadCell(ws, r, link, fillRgb);
      setCell(ws, r, 2, formatScore(asset.score), groupCell);
      setCell(ws, r, 3, EMPTY_CELL_TOKEN, base);
      setCell(ws, r, 4, titleCase(asset.status), statusStyle(asset.status, fillRgb));
      setCell(ws, r, 5, EMPTY_CELL_TOKEN, base);
      setCell(ws, r, 6, asset.error ?? "", base);
      r += 1;
      return;
    }

    features.forEach((feature, i) => {
      const rowR = startR + i;
      if (i === 0) {
        setCell(ws, rowR, 0, label, groupCell);
        setDownloadCell(ws, rowR, link, fillRgb);
        setCell(ws, rowR, 2, formatScore(asset.score), groupCell);
      } else {
        setCell(ws, rowR, 0, "", groupCell);
        setCell(ws, rowR, 1, "", groupCell);
        setCell(ws, rowR, 2, "", groupCell);
      }
      // Display state so unweighted-but-scored criteria read as Pass/Fail, not "Not weighted".
      const displayState = featureDisplayState(feature);
      setCell(ws, rowR, 3, feature.name, base);
      setCell(ws, rowR, 4, humanizeState(displayState), statusStyle(displayState, fillRgb));
      setCell(ws, rowR, 5, formatWeight(feature.weight, feature.state), base);
      setCell(ws, rowR, 6, formatNotes(featureDetails(feature)), base);
    });

    const endR = startR + features.length - 1;
    if (endR > startR) {
      mergeRange(ws, startR, 0, endR, 0);
      mergeRange(ws, startR, 1, endR, 1);
      mergeRange(ws, startR, 2, endR, 2);
    }
    r = endR + 1;
  });

  ws["!cols"] = [
    { wch: 34 }, // Asset
    { wch: 12 }, // Download
    { wch: 11 }, // Asset Score
    { wch: 22 }, // Criterion
    { wch: 15 }, // Status
    { wch: 9 }, // Weight
    { wch: 50 }, // Notes
  ];
  ws["!rows"] = [{ hpt: 30 }];
  ws["!sheetViews"] = [{ showGridLines: false }];
  updateRef(ws);
  return ws;
}

/** Two-sheet styled workbook: a report-stats Summary and a grouped Criteria table. */
export function buildReportWorkbook(report: PreflightReportDetail): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(report), "Summary");
  XLSX.utils.book_append_sheet(wb, buildCriteriaSheet(report), "Criteria");
  return wb;
}

/** Serialize the workbook to raw xlsx bytes (used by the browser download + tests). */
export function buildReportWorkbookArray(report: PreflightReportDetail): Uint8Array {
  return XLSX.write(buildReportWorkbook(report), {
    type: "array",
    bookType: "xlsx",
  }) as Uint8Array;
}

export function exportReportToXlsx(report: PreflightReportDetail): void {
  downloadBytes(
    buildReportWorkbookArray(report),
    exportFilename(report, "xlsx"),
    XLSX_MIME,
  );
}

export function exportAssetToXlsx(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
): void {
  downloadBytes(
    buildReportWorkbookArray(singleAssetReport(report, asset)),
    assetExportFilename(report, asset, "xlsx"),
    XLSX_MIME,
  );
}
